/**
 * Every key, rebindable, as one knob.
 *
 * One setting rather than one per action, because the value is the map: a conflict is a fact about
 * two rows, the control that owns the whole map is the only thing that can settle one, and a save
 * holding `{ sprint: ["ShiftLeft"] }` under a single key still reads as what the player chose.
 *
 * `apply` hands the map to [keyBindingStore](../../../core/input/bindings/keyBindingStore.ts),
 * which every control map reads per tick, so a rebind works on the very next frame with a game
 * running behind the screen. A stored value this build cannot read falls back to the defaults
 * whole, which is the same three-answer shape every other knob's coercion states.
 */

import { fromStored, toStored } from '@/core/input/bindings/bindingOps';
import { DEFAULT_BINDINGS } from '@/core/input/bindings/defaultBindings';
import { setBindingMap } from '@/core/input/bindings/keyBindingStore';
import { SETTING_KEY_BINDINGS_HINT, SETTING_KEY_BINDINGS_LABEL } from '@/text/settingsText';
import type { SettingDefinition, SettingValue } from '@/ui/settings/settingContract';

export const KEY_BINDINGS: SettingDefinition = {
  id: 'keyBindings',
  section: 'controls',
  label: SETTING_KEY_BINDINGS_LABEL,
  hint: SETTING_KEY_BINDINGS_HINT,
  control: { kind: 'bindings' },
  defaultValue: toStored(DEFAULT_BINDINGS),
  apply: (value: SettingValue): void => {
    const map = fromStored(value);
    if (map !== null) setBindingMap(map);
  },
};
